import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Avatar, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Spin, 
  Empty,
  Row,
  Col,
  Divider,
  Table,
  Input,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined as PendingIcon,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { MentorBookingResponse } from '../../../types';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

const { Title, Text, Paragraph } = Typography;

const OneOnOneRequestsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    dayjs.locale('tr');
  }, []);

  const { data: bookingsResult, isLoading: loading, refetch } = useQuery({
    queryKey: [menteeProfileQueryKeys.bookings, page],
    queryFn: async () => {
      const res = await apiService.getMentorBookingsByMentee(page, pageSize);
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });
  const bookings = bookingsResult?.data ?? [];
  const totalCount = bookingsResult?.totalCount ?? 0;

  const cancelMutation = useMutation({
    mutationFn: (bookingId: number) => apiService.cancelMentorBooking(bookingId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Randevu iptal edildi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.bookings });
      } else toast.error(res.message || 'Randevu iptal edilemedi.');
    },
    onError: () => toast.error('Randevu iptal edilirken hata oluştu.'),
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'orange';
      case 'Confirmed':
        return 'green';
      case 'Cancelled':
        return 'red';
      case 'Completed':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pending':
        return <PendingIcon />;
      case 'Confirmed':
        return <CheckCircleOutlined />;
      case 'Cancelled':
        return <CloseCircleOutlined />;
      case 'Completed':
        return <CheckCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Confirmed':
        return 'Onaylandı';
      case 'Cancelled':
        return 'İptal Edildi';
      case 'Completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  };

  const handleCancelBooking = (bookingId: number) => cancelMutation.mutate(bookingId);

  const canCancelBooking = (startTime: string) => {
    const bookingTime = dayjs(startTime);
    const now = dayjs();
    const hoursUntilBooking = bookingTime.diff(now, 'hour');
    return hoursUntilBooking >= 24;
  };

  const filteredBookings = (bookings || []).filter(booking =>
    booking.mentor.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
    booking.mentor.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
    booking.status.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Mentor',
      dataIndex: 'mentor',
      key: 'mentor',
      render: (mentor: any) => (
        <Space>
          <Avatar 
            size={40} 
            src={mentor.profileImage ? `data:image/jpeg;base64,${mentor.profileImage}` : undefined}
            icon={<UserOutlined />}
          />
          <div>
            <Text strong>{mentor.firstName} {mentor.lastName}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (startTime: string) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(startTime).format('DD MMMM YYYY')}</Text>
          <Text type="secondary">{dayjs(startTime).format('dddd')}</Text>
        </Space>
      ),
    },
    {
      title: 'Saat',
      dataIndex: 'startTime',
      key: 'time',
      render: (startTime: string, record: MentorBookingResponse) => (
        <Text>{dayjs(startTime).format('HH:mm')} - {dayjs(record.endTime).format('HH:mm')}</Text>
      ),
    },
    {
      title: 'Ücret',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <Text strong style={{ color: '#059669' }}>₺{price}</Text>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag 
          color={getStatusColor(status)} 
          icon={getStatusIcon(status)}
          style={{ fontSize: '12px', padding: '4px 8px' }}
        >
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: MentorBookingResponse) => (
        <Space>
          {record.status === 'Pending' && (
            canCancelBooking(record.startTime) ? (
              <Button 
                danger 
                size="small" 
                onClick={() => handleCancelBooking(record.id)}
              >
                İptal Et
              </Button>
            ) : (
              <Tooltip title="Randevu başlangıcından 24 saatten az kaldığı için iptal edilemez">
                <Button 
                  danger 
                  size="small" 
                  disabled
                >
                  İptal Et
                </Button>
              </Tooltip>
            )
          )}
          {record.meetingLink && (
            <Button 
              type="link" 
              href={record.meetingLink} 
              target="_blank"
              size="small"
            >
              Toplantı
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder="Mentor adı veya durum ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
          loading={loading}
        >
          Yenile
        </Button>
      </div>

      {filteredBookings.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={searchText ? "Arama kriterlerinize uygun randevu bulunamadı" : "Henüz bire-bir randevu talebiniz bulunmuyor"}
          style={{ marginTop: '50px' }}
        />
      ) : (
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredBookings}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} randevu`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
            },
            onShowSizeChange: (current, size) => {
              setPage(1);
            },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '6px' }}>
                {record.notes && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong>Notlar: </Text>
                    <Text>{record.notes}</Text>
                  </div>
                )}
                <div>
                  <Text strong>Oluşturulma: </Text>
                  <Text type="secondary">{dayjs(record.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                </div>
              </div>
            ),
            rowExpandable: (record) => !!(record.notes || true),
          }}
        />
      )}
    </div>
  );
};

export default OneOnOneRequestsTab;
